import { UserSummaryDto } from "@/api/user/dto";
import {
  HasChildren,
  MaritalStatus,
  SubmitMyProfileAndAnswersRequestDto,
  useSubmitMyProfileAndAnswers,
} from "@/api/user/useSubmitMyProfileAndAnswers";
import { useAuth } from "@/auth/AuthContext";
import { usePostHogQuestionnaireTracking } from "@/hooks/usePostHogQuestionnaireTracking";
import { QuestionnaireFlowQuestion } from "@/types/question.types";
import { Center, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { QuestionnaireFlow } from "../shared/QuestionnaireFlow";
import { BaseQuestionnaireFormValues } from "./QuestionAndAnswerResolver";
import { savePersonalityTestFormData } from "./personalityTestStorage";
import { analyzeQuestion } from "./questionAnalyzer";

type Props = {
  questions: QuestionnaireFlowQuestion[];
  isPersonalityTestOnly?: boolean;
  userSummary?: UserSummaryDto;
};

export interface PersonalityFormValues extends BaseQuestionnaireFormValues {
  personal_info: {
    gender: string;
    groupGenderPreference: string | null;
    maritalStatus: MaritalStatus | null;
    hasChildren: HasChildren | null;
    profession: string;
    nationality: string;
    birthDate: string;
    groupAgePreference: string | null;
  };
  answers: {
    [questionId: number]: {
      answerText?: string;
      answerScale?: number;
      answerOptions?: number[];
      answerDate?: string;
    };
  };
}

export function PersonalityTest({ questions, isPersonalityTestOnly = false, userSummary }: Props) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const {
    mutateAsync: submitPersonalityTest,
    isPending: isSubmitPending,
    isError: isSubmitError,
    isSuccess: isSubmitSuccess,
  } = useSubmitMyProfileAndAnswers();

  const navigate = useNavigate();

  const { trackFlowStarted, trackFlowCompleted } = usePostHogQuestionnaireTracking();

  useEffect(() => {
    trackFlowStarted("personality_test");
  }, []);

  const form = useForm<PersonalityFormValues>({
    initialValues: {
      personal_info: {
        gender: "",
        groupGenderPreference: null,
        maritalStatus: null,
        hasChildren: null,
        profession: "",
        nationality: "",
        birthDate: "",
        groupAgePreference: null,
      },
      answers: questions.reduce(
        (acc, q) => {
          const analysis = analyzeQuestion(q);
          if (analysis.isPersonalInfo) return acc;
          if (!q.id) return acc;

          acc[q.id] = {};
          return acc;
        },
        {} as PersonalityFormValues["answers"]
      ),
    },
    validateInputOnChange: true,
    validate: {
      personal_info: isPersonalityTestOnly
        ? undefined
        : {
            nationality: (value) => (value.length === 0 ? "필수 입력 항목입니다." : null),
            birthDate: (value) => {
              if (!value || value.trim() === "") {
                return "생년월일을 입력해주세요.";
              }
              if (value.length < 8) {
                return "유효한 형식(YYYYMMDD)으로 입력해주세요.";
              }
              if (isNaN(new Date(value).getTime())) {
                return "유효하지 않은 날짜입니다.";
              }
              return null;
            },
            groupGenderPreference: (value) => (!value ? "필수 입력 항목입니다." : null),
            groupAgePreference: (value) => (!value ? "필수 입력 항목입니다." : null),
          },
      answers: questions.reduce(
        (acc, q) => {
          const analysis = analyzeQuestion(q);
          if (analysis.isPersonalInfo) return acc;
          if (!q.id) return acc;
          acc[q.id] = (answer: PersonalityFormValues["answers"][number]) => {
            if (analysis.isTextQuestion) {
              if (!answer?.answerText || answer.answerText.trim().length === 0)
                return "필수 입력 항목입니다.";
            }
            if (analysis.isSelectMultipleQuestion || analysis.isSelectFromListQuestion) {
              if (!answer?.answerOptions || answer.answerOptions.length === 0)
                return "필수 입력 항목입니다.";
            }
            if (analysis.isDateQuestion) {
              if (!answer?.answerDate || isNaN(new Date(answer.answerDate).getTime()))
                return "필수 입력 항목입니다.";
            }
            if (analysis.isRangeQuestion) {
              if (answer?.answerScale === undefined || answer?.answerScale === null)
                return "필수 입력 항목입니다.";
            }
            return null;
          };
          return acc;
        },
        {} as Record<number, (answer: PersonalityFormValues["answers"][number]) => string | null>
      ),
    },
  });

  const getQuestionPaths = (q: QuestionnaireFlowQuestion) => {
    const analysis = analyzeQuestion(q);
    let fieldPath = `personal_info.${q.slug}`;
    let validationPath = fieldPath;

    if (q.category === "custom") {
      if (analysis.isTextQuestion) fieldPath = `answers.${q.id}.answerText`;
      if (analysis.isRangeQuestion) fieldPath = `answers.${q.id}.answerScale`;
      if (analysis.isDateQuestion) fieldPath = `answers.${q.id}.answerDate`;
      if (analysis.isSelectQuestion) fieldPath = `answers.${q.id}.answerOptions`;
      validationPath = `answers.${q.id}`;
    }
    return { fieldPath, validationPath };
  };

  const saveFormDataToSessionStorageAndNavigateToGuestLogin = () => {
    savePersonalityTestFormData(form.values);
    navigate("/signup");
  };

  const onSubmitCallback = async () => {
    if (form.validate().hasErrors) {
      notifications.show({
        title: "오류",
        message: "오류가 발생했습니다. 다시 시도해주세요.",
        color: "red",
      });
      return;
    }

    if (!!user) {
      const { personal_info, ...answersOnly } = form.values;
      const submissionData = isPersonalityTestOnly ? answersOnly : form.values;
      try {
        await submitPersonalityTest(
          submissionData as unknown as SubmitMyProfileAndAnswersRequestDto
        );
        await trackFlowCompleted("personality_test", questions.length);
        if (userSummary?.hasCompletedSignupFlow) {
          navigate("/home");
        } else {
          if (user.app_metadata.provider === "kakao") {
            navigate("/signup/kakao");
          } else {
            navigate("/signup");
          }
        }
      } catch (error) {
        notifications.show({
          title: "오류",
          message: "오류가 발생했습니다. 다시 시도해주세요.",
          color: "red",
        });
      }
    } else {
      await trackFlowCompleted("personality_test", questions.length);
      saveFormDataToSessionStorageAndNavigateToGuestLogin();
    }
  };

  const isKakaoUser = user?.app_metadata.provider === "kakao";

  if (!questions || questions.length === 0) {
    return (
      <Center h="100%">
        <Text>Loading questions or no questions available.</Text>
      </Center>
    );
  }
  if (isSubmitError) {
    return (
      <Center h="100%">
        <Text>Error submitting personality test</Text>
      </Center>
    );
  }

  return (
    <QuestionnaireFlow
      flowType="personality_test"
      questions={questions}
      getQuestionPaths={getQuestionPaths}
      form={form}
      onSubmit={onSubmitCallback}
      isSubmitting={isSubmitPending}
      isSubmitError={isSubmitError}
      onUnauthenticatedSubmit={() => {
        saveFormDataToSessionStorageAndNavigateToGuestLogin();
      }}
    />
  );
}
